import React, { useEffect, useState } from 'react';
import { Layout, Table, Button, Tag, Select, Space, Typography, Card, App, Modal, Input, Tooltip, Popconfirm } from 'antd';
import { tc } from '../../i18n/common';
import axios from 'axios';
import { DownloadOutlined, MailOutlined, ArrowLeftOutlined, DeleteOutlined, CopyOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const AdminTaskDashboard = () => {
    // ... hooks
    const { message } = App.useApp();
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Notification Modal State
    const [notifyModalOpen, setNotifyModalOpen] = useState(false);
    const [currentTask, setCurrentTask] = useState(null);
    const [emailContent, setEmailContent] = useState('');
    const [sending, setSending] = useState(false);
    
    // Changes Modal State
    const [changesModalOpen, setChangesModalOpen] = useState(false);
    const [changesData, setChangesData] = useState([]);

    // Bulk Selection State
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [bulkLoading, setBulkLoading] = useState(false);

    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const handleBulkStatus = async (status) => {
        setBulkLoading(true);
        try {
            await Promise.all(selectedRowKeys.map(id => axios.patch(`/admin/tasks/${id}/status`, { status })));
            message.success(`Status updated for ${selectedRowKeys.length} tasks`);
            setTasks(prev => prev.map(t => selectedRowKeys.includes(t.id) ? { ...t, status } : t));
            setSelectedRowKeys([]);
        } catch (error) {
            console.error('Bulk update error:', error);
            message.error('Failed to update status for some tasks');
        } finally {
            setBulkLoading(false);
        }
    };

    const handleBulkNotify = async () => {
        setBulkLoading(true);
        try {
            // Sequential execution to avoid rate limits or overwhelming the server/mail
            for (const id of selectedRowKeys) {
                 await axios.post(`/admin/tasks/${id}/notify`);
            }
            message.success(`Notifications sent to Admin for ${selectedRowKeys.length} tasks`);
            setSelectedRowKeys([]);
        } catch (error) {
            console.error('Bulk notify error:', error);
            message.error('Failed to send some notifications');
        } finally {
            setBulkLoading(false);
        }
    };

    const handleBulkDelete = async () => {
        setBulkLoading(true);
        try {
            await Promise.all(selectedRowKeys.map(id => axios.delete(`/tasks/${id}`)));
            message.success(`Deleted ${selectedRowKeys.length} tasks`);
            setTasks(prev => prev.filter(t => !selectedRowKeys.includes(t.id)));
            setSelectedRowKeys([]);
        } catch (error) {
            console.error('Bulk delete error:', error);
            message.error('Failed to delete some tasks');
        } finally {
            setBulkLoading(false);
        }
    };


    const changesColumns = [
        { title: 'Type', dataIndex: 'type', key: 'type', width: 100 },
        { title: 'Person', dataIndex: 'person', key: 'person', width: 200 },
        { title: 'Organization', dataIndex: 'org', key: 'org', width: 200 },
        { title: 'Description / Diff', dataIndex: 'description', key: 'desc', render: (text, r) => (
            <div style={{whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12}}>{text || r.details}</div>
        )}
    ];

    const openChangesModal = (task) => {
        if (!task || !task.changes) return;
        
        const rows = task.changes.map((c, index) => {
             // 1. Try to get context
             let ctx = c.details && c.details.exportContext ? c.details.exportContext : {};
             let user = ctx.user || {};
             let org = ctx.org || {};

             // 2. Fallback: Check params if context is missing
             if (!user.mail && c.params) {
                 if (c.params.user && c.params.user.mail) {
                     user = c.params.user;
                 } else if (c.params.mail) {
                     user = c.params;
                 }
             }
             if (!org.name && c.params) {
                if (c.params.org && c.params.org.name) {
                    org = c.params.org;
                } else if (c.params.organizationName) {
                    org = { name: c.params.organizationName, externalId: c.params.organizationId };
                }
             }

             // 3. Special handling for Deletions (ORG_DELETE, USER_REMOVE)
             if ((!org.name && !ctx.name) && (c.type === 'ORG_DELETE' || c.key === 'logs.org_delete')) {
                 if (c.params) {
                     org = {
                         externalId: c.params.id || '',
                         name: c.params.name || ''
                     };
                     if(c.details && c.details.id) org.externalId = c.details.id;
                 }
             }

             // 4. Construct the row data
             let personName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
             if (!personName) {
                  // Last ditch attempt from params
                  if (c.params && (c.params.firstName || c.params.lastName)) {
                      personName = `${c.params.firstName || ''} ${c.params.lastName || ''}`.trim();
                  }
             }
             
             let orgName = org.name || ctx.name || '';
             if (!orgName && c.org_name) orgName = c.org_name; // sometimes stored flat?

             let row = {
                 key: index,
                 type: tc(`types.${c.type.toLowerCase()}`) || c.type,
                 description: '',
                 details: c.details && c.details.formattedDiff ? c.details.formattedDiff : '',
                 person: personName || '-',
                 org: orgName || '-'
             };

             if (user.externalId) row.person += ` (${user.externalId})`;
             
             // Use formatDescription for description column
             row.description = formatDescription(c);

             return row;
        });
        setChangesData(rows);
        setChangesModalOpen(true);
    };

    const fetchTasks = async () => {
        // ... existing implementation
        try {
            setLoading(true);
            const res = await axios.get('/admin/tasks');
            setTasks(res.data);
        } catch (error) {
            console.error('Error fetching tasks:', error);
            message.error('Failed to load tasks');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTasks();
    }, []);

    const handleStatusChange = async (id, status) => {
        // ... existing
        try {
            await axios.patch(`/admin/tasks/${id}/status`, { status });
            message.success('Status updated');
            setTasks(prev => prev.map(task => task.id === id ? { ...task, status } : task));
        } catch (error) {
            console.error('Error updating status:', error);
            message.error('Failed to update status');
        }
    };

    const generateEmailContent = (task) => {
        if (!task) return '';
        const statusMap = {
            pending: 'Pending',
            in_progress: 'In progress',
            done: 'Done',
            canceled: 'Canceled'
        };
        const statusText = statusMap[task.status] || task.status;
        const name = task.user?.name || 'User';
        
        let text = `Hallo ${name},\n\n`;
        text += `Es gibt ein Update zu Ihrer Anfrage: "${task.description}".\n`;
        text += `Status: ${statusText}.\n`;
        
        if (task.status === 'done') {
            text += `Die Änderungen sind jetzt online!\n`;
        }
        
        text += `\nFreundliche Grüsse,\nIhr Quantonomics Team`;
        return text;
    };

    const openNotifyModal = (task) => {
        setCurrentTask(task);
        setEmailContent(generateEmailContent(task));
        setNotifyModalOpen(true);
    };

    const handleSendNotify = async () => {
        if (!currentTask) return;
        setSending(true);
        try {
            await axios.post(`/admin/tasks/${currentTask.id}/notify`);
            message.success('Notification sent to Admin (for forwarding)');
            setNotifyModalOpen(false);
        } catch (error) {
            console.error('Error sending notification:', error);
            message.error('Failed to send notification');
        } finally {
            setSending(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(emailContent).then(() => {
            message.success('Email content copied!');
        });
    };

    const handleDelete = async (id) => {
        // ... existing
        try {
            await axios.delete(`/tasks/${id}`);
            message.success(tc('common.deleted_successfully') || 'Deleted successfully');
            setTasks(prev => prev.filter(task => task.id !== id));
        } catch (error) {
            console.error('Error deleting task:', error);
            message.error('Failed to delete task');
        }
    };
    
    const formatDescription = (change) => {
        let text = change.key ? tc(change.key, change.params) : (change.desc || '');
        
        // Append Role if available (for USER_ADD/USER_REMOVE)
        if (change.params && change.params.role) {
            const roleMap = {
                'leaders': 'Leader',
                'mitarbeiter': 'Employee',
                'viewers': 'Viewer' // or 'Giver'? User mentioned "Giver"
            };
            // Use translation if available or fallback
            // Assuming tc() works for generic keys, or just hardcode/map for now as requested
            // User requested: "Added as Giver/Leader"
            const roleLabel = roleMap[change.params.role] || change.params.role;
            text += ` (${roleLabel})`;
        }

        if (change.details && change.details.formattedDiff) {
            text += `\n(${change.details.formattedDiff})`;
        }
        return text;
    };

    const exportMD = (task) => {
        if (!task || !task.changes) return;
        const content = task.changes.map(c => {
             const desc = formatDescription(c);
             return `[${c.timestamp || new Date(task.created_at).toISOString()}] ${c.type}: ${desc}`;
        }).join('\n');
        
        const timestamp = new Date(task.created_at).toISOString().split('T')[0];
        const filename = `Task_${task.id}_Changes_${timestamp}.md`;

        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleDownload = async (taskId, file) => {
        try {
            const response = await axios.get(`/tasks/${taskId}/file/${file.id}`, {
                responseType: 'blob', // Important for binary data
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', file.file_name);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download error:', error);
            message.error('Failed to download file. You might not have permission.');
        }
    };

    const exportXLSX = (task) => {
        if (!task || !task.changes) return;
        import('xlsx').then(XLSX => {
            const rows = task.changes.map(c => {
                 // Try to get context
                 let ctx = c.details && c.details.exportContext ? c.details.exportContext : {};
                 let user = ctx.user || {};
                 let org = ctx.org || {};

                 // Fallback: Check params if context is missing (common for Add/Update logic sometimes)
                 if (!user.mail && c.params) {
                     // Check if params has flat user structure or nested
                     if (c.params.user && c.params.user.mail) {
                         user = c.params.user;
                     } else if (c.params.mail) {
                         user = c.params; // Assume params is the user object
                     }
                 }
                 if (!org.name && c.params) {
                    if (c.params.org && c.params.org.name) {
                        org = c.params.org;
                    } else if (c.params.organizationName) {
                        org = { name: c.params.organizationName, externalId: c.params.organizationId };
                    }
                 }

                 // Fallback for Organization Deletion (where context might be minimal or missing 'org' object)
                 // If c.params has org data, use it. Usually for ORG_DELETE params has { id, name }
                 if ((!org.name && !ctx.name) && (c.type === 'ORG_DELETE' || c.key === 'logs.org_delete')) {
                     if (c.params) {
                         // Attempt to fill org from params
                         org = {
                             externalId: c.params.id || '', // or however it's stored
                             name: c.params.name || ''
                         };
                         // Sometimes ID is just passed as c.params.id, verify structure
                         if(c.details && c.details.id) org.externalId = c.details.id;
                     }
                 }
                 
                 // If no context, try to infer from params if possible, or leave empty
                 // But primarily we rely on exportContext being present for rich data.

                 return {
                    "Person ID": user.externalId || ctx.id || (c.params?.externalId) || "",
                    "Last Name": user.lastName || ctx.lastName || (c.params?.lastName) || "",
                    "First Name": user.firstName || ctx.firstName || (c.params?.firstName) || "",
                    "E-Mail": user.mail || (c.params?.mail) || "",
                    "Organisation ID": org.externalId || ctx.externalId || (c.params ? c.params.id : "") || "", 
                    "Organisation Name": org.name || ctx.name || (c.params ? c.params.name : "") || "",
                    "Superior Organisation ID": org.parentIdExternal || ctx.parentOrgId || "",
                    "Superior Organisation Name": org.parentName || ctx.parentOrgName || "",
                    "Role": user.jobTitle || ctx.role || "",
                    "Gender": "",
                    "Superior ID": user.derivedSupExternalId || "",
                    "Superior Last Name": user.derivedSupLastName || "",
                    "Superior First Name": user.derivedSupFirstName || "",
                    "Language": user.language || user.preferredLanguage || "",
                    
                    // Extra Context
                    "Change Type": tc(`types.${c.type.toLowerCase()}`) || tc(c.type) || c.type,
                    "Change Details": formatDescription(c)
                };
            });
            const worksheet = XLSX.utils.json_to_sheet(rows);
            // Auto-width columns roughly
            const wscols = Object.keys(rows[0] || {}).map(k => ({ wch: 20 }));
            worksheet['!cols'] = wscols;

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Task Changes");
            const timestamp = new Date(task.created_at).toISOString().split('T')[0];
            const filename = `Task_${task.id}_Changes_${timestamp}.xlsx`;
            XLSX.writeFile(workbook, filename);
        });
    };
    
    const getClientDomain = (task) => {
        if (!task || !task.changes) return '-';
        const domains = {};
        task.changes.forEach(c => {
            let email = null;
            if (c.details && c.details.exportContext && c.details.exportContext.user && c.details.exportContext.user.mail) {
                email = c.details.exportContext.user.mail;
            }
            if (!email && c.params && c.params.email) {
                email = c.params.email;
            }
            if (email && typeof email === 'string' && email.includes('@')) {
                const domain = email.split('@')[1].toLowerCase();
                domains[domain] = (domains[domain] || 0) + 1;
            }
        });
        if (Object.keys(domains).length === 0) return '-';
        return Object.entries(domains).reduce((a, b) => b[1] > a[1] ? b : a)[0];
    };

    const columns = [
        // ... previous columns
        {
            title: 'Date',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => new Date(text).toLocaleDateString(),
            sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
        },
        {
            title: 'Client',
            key: 'client',
            render: (_, record) => <Tag>{record.client_domain || getClientDomain(record)}</Tag>
        },
        {
            title: tc('organigram_ui.users'),
            dataIndex: ['user', 'name'],
            key: 'user',
            render: (text, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{text || 'Unknown User'}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>{record.user?.email}</Text>
                </Space>
            )
        },
        {
            title: tc('dashboard.tasks.description_label'),
            dataIndex: 'description',
            key: 'description',
            width: '30%',
            render: (text) => (
                <div style={{ whiteSpace: 'pre-wrap', maxHeight: 150, overflowY: 'auto' }}>
                    {text}
                </div>
            )
        },
        {
            title: tc('dashboard.tasks.attachments_label'),
            dataIndex: 'files',
            key: 'files',
            render: (files, record) => (
                <Space direction="vertical">
                    {files && files.map(file => (
                        <Button 
                            key={file.id} 
                            type="link" 
                            icon={<DownloadOutlined />} 
                            onClick={() => handleDownload(record.id, file)}
                            size="small"
                            style={{ padding: 0 }}
                        >
                            {file.file_name}
                        </Button>
                    ))}
                    {(!files || files.length === 0) && <Text type="secondary">-</Text>}
                </Space>
            )
        },
        {
            title: 'Downloads',
            key: 'downloads',
            render: (_, record) => (
                <Space direction="vertical" size="small">
                    <Button size="small" icon={<EyeOutlined />} onClick={() => openChangesModal(record)}>View</Button>
                    <Button size="small" onClick={() => exportMD(record)}>{tc('export_md') || 'MD'}</Button>
                    <Button size="small" onClick={() => exportXLSX(record)}>{tc('export_excel') || 'XLSX'}</Button>
                </Space>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status, record) => (
                <Select
                    defaultValue={status}
                    style={{ width: 140 }}
                    onChange={(val) => handleStatusChange(record.id, val)}
                >
                    <Option value="pending"><Tag color="orange">PENDING</Tag></Option>
                    <Option value="in_progress"><Tag color="blue">IN PROGRESS</Tag></Option>
                    <Option value="done"><Tag color="green">DONE</Tag></Option>
                    <Option value="canceled"><Tag color="red">CANCELED</Tag></Option>
                </Select>
            ),
            filters: [
                { text: 'Pending', value: 'pending' },
                { text: 'In Progress', value: 'in_progress' },
                { text: 'Done', value: 'done' },
            ],
            onFilter: (value, record) => record.status === value,
        },
        {
            title: 'Actions',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Preview & Send Email to Admin">
                        <Button 
                            icon={<MailOutlined />} 
                            onClick={() => openNotifyModal(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title={tc('common.delete_confirm') || "Are you sure?"}
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button 
                            danger
                            icon={<DeleteOutlined />} 
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Layout style={{ minHeight: '100vh', background: '#f4f4f9' }}>
             <header style={{ 
                background: '#fff', 
                padding: '0 24px', 
                height: 64, 
                display: 'flex', 
                alignItems: 'center',
                borderBottom: '1px solid #f0f0f0'
            }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/organigram')} style={{ marginRight: 16 }} />
                <Title level={4} style={{ margin: 0 }}>Task Management Dashboard</Title>
            </header>
            <Content style={{ padding: '24px', maxWidth: 1400, margin: '0 auto', width: '100%' }}>
                <Card>
                    {selectedRowKeys.length > 0 && (
                        <div style={{ marginBottom: 16, padding: '8px 16px', background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Space>
                                <Text strong>{selectedRowKeys.length} selected</Text>
                                <Select 
                                    placeholder="Bulk Status Change" 
                                    style={{ width: 200 }} 
                                    onChange={handleBulkStatus}
                                    disabled={bulkLoading}
                                >
                                    <Option value="pending">Set Pending</Option>
                                    <Option value="in_progress">Set In Progress</Option>
                                    <Option value="done">Set Done</Option>
                                    <Option value="canceled">Set Canceled</Option>
                                </Select>
                                <Popconfirm
                                    title={`Send notification for ${selectedRowKeys.length} tasks?`}
                                    onConfirm={handleBulkNotify}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <Button icon={<MailOutlined />} loading={bulkLoading}>
                                        Notify Selected
                                    </Button>
                                </Popconfirm>
                                <Popconfirm
                                    title={`Delete ${selectedRowKeys.length} tasks?`}
                                    onConfirm={handleBulkDelete}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <Button danger icon={<DeleteOutlined />} loading={bulkLoading}>
                                        Delete Selected
                                    </Button>
                                </Popconfirm>
                            </Space>
                            <Button type="link" onClick={() => setSelectedRowKeys([])}>Clear Selection</Button>
                        </div>
                    )}
                    <Table
                        rowSelection={rowSelection}
                        columns={columns}
                        dataSource={tasks}
                        rowKey="id"
                        loading={loading}
                        pagination={{ pageSize: 20 }}
                    />
                </Card>

                <Modal
                    title="Notify User (via Admin)"
                    open={notifyModalOpen}
                    onCancel={() => setNotifyModalOpen(false)}
                    footer={[
                        <Button key="cancel" onClick={() => setNotifyModalOpen(false)}>
                            Cancel
                        </Button>,
                        <Button key="send" type="primary" loading={sending} onClick={handleSendNotify} icon={<MailOutlined />}>
                            Send to Admin
                        </Button>
                    ]}
                >
                    <div style={{ marginBottom: 16 }}>
                        <Text type="secondary">
                            The following content will be sent to the <b>Admin</b> email (so you can forward it).
                        </Text>
                    </div>
                    <Input.TextArea 
                        rows={10} 
                        value={emailContent} 
                        readOnly 
                        style={{ marginBottom: 16, backgroundColor: '#f5f5f5', fontFamily: 'monospace' }} 
                    />
                    <Button icon={<CopyOutlined />} onClick={copyToClipboard} block>
                        Copy Content
                    </Button>
                </Modal>

                <Modal 
                   title="Task Changes Details"
                   open={changesModalOpen}
                   onCancel={() => setChangesModalOpen(false)}
                   footer={[<Button key="close" onClick={() => setChangesModalOpen(false)}>Close</Button>]}
                   width={1200}
                >
                    <Table dataSource={changesData} columns={changesColumns} pagination={false} size="small" scroll={{ y: 600 }} />
                </Modal>
            </Content>
        </Layout>
    );
};

export default AdminTaskDashboard;
